import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../../contexts/ThemeContext';
import { authFetch } from '../../services/authFetch';

const LaporLemburForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    start_overtime_date: '',
    end_overtime_date: '',
    start_time: '',
    finish_time: '',
    overtime_hours: '',
    purpose: '',
    type_or_work: '',
    teacher_name: '',
    teacher_id: '',
    student_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [teachers, setTeachers] = useState([]);
  const [teacherSuggestions, setTeacherSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  
  // Student selection states
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Fetch teachers and students on component mount
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await authFetch('/api/teacher/teachers/praktek');
        if (response.ok) {
          const result = await response.json();
          // Handle both paginated and direct array responses
          const teachersData = result.data || result;
          setTeachers(Array.isArray(teachersData) ? teachersData : []);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };
    
    const fetchStudents = async () => {
      try {
        const response = await authFetch('/api/kts-students');
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setStudents(result.data || []);
          }
        }
      } catch (error) {
        console.error('Error fetching students:', error);
      }
    };
    
    fetchTeachers();
    fetchStudents();
  }, []);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData({
        start_overtime_date: initialData.start_overtime_date || '',
        end_overtime_date: initialData.end_overtime_date || '',
        start_time: initialData.start_time || '',
        finish_time: initialData.finish_time || '',
        overtime_hours: initialData.overtime_hours || '',
        purpose: initialData.purpose || '',
        type_or_work: initialData.type_or_work || '',
        teacher_name: initialData.teacher_name || '',
        teacher_id: initialData.teacher_id || '',
        student_id: initialData.student_id || ''
      });
      if (initialData.teacher_id) {
        setSelectedTeacherId(initialData.teacher_id);
      }
      if (initialData.student_id) {
        const student = students.find(s => s.id === initialData.student_id);
        setSelectedStudent(student);
        setStudentSearch(student ? student.name : '');
      }
    }
  }, [initialData]);

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    
    if (name === 'teacher_name') {
      setFormData({ ...formData, teacher_name: value, teacher_id: '' });
      setSelectedTeacherId(null);
      
      // Filter teachers based on input
      if (value.trim()) {
        const filtered = teachers.filter(teacher => 
          teacher.name.toLowerCase().includes(value.toLowerCase())
        );
        setTeacherSuggestions(filtered);
        setShowSuggestions(true);
      } else {
        setTeacherSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTeacherSelect = (teacher) => {
    setFormData({ 
      ...formData, 
      teacher_name: teacher.name,
      teacher_id: teacher.id 
    });
    setSelectedTeacherId(teacher.id);
    setShowSuggestions(false);
    setTeacherSuggestions([]);
  };

  const handleTeacherInputBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Helper functions for student selection
  const selectStudent = (student) => {
    setSelectedStudent(student);
    setFormData({ ...formData, student_id: student.id });
    setStudentSearch(student.name);
    setShowStudentSuggestions(false);
  };

  const clearSelectedStudent = () => {
    setSelectedStudent(null);
    setFormData({ ...formData, student_id: '' });
    setStudentSearch('');
  };

  const handleStudentInputBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => {
      setShowStudentSuggestions(false);
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    setNotification({ show: false, message: '', type: 'success' });
    setLoading(true);

    // Validate overtime hours
    if (formData.overtime_hours && (isNaN(formData.overtime_hours) || parseFloat(formData.overtime_hours) <= 0)) {
      setNotification({ 
        show: true, 
        message: 'Jumlah jam lembur harus berupa angka positif.', 
        type: 'error' 
      });
      setLoading(false);
      return;
    }

    // Validate date range
    if (formData.start_overtime_date && formData.end_overtime_date) {
      const startDate = new Date(formData.start_overtime_date);
      const endDate = new Date(formData.end_overtime_date);
      
      if (endDate < startDate) {
        setNotification({ 
          show: true, 
          message: 'Tanggal lembur selesai tidak boleh lebih awal dari tanggal lembur mulai.', 
          type: 'error' 
        });
        setLoading(false);
        return;
      }
    }
    
    // Check for cross-midnight scenarios and warn user (only if finish_time is provided)
    if (formData.start_time && formData.finish_time) {
      const [startHour, startMinute] = formData.start_time.split(':').map(Number);
      const [finishHour, finishMinute] = formData.finish_time.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;
      const finishTotalMinutes = finishHour * 60 + finishMinute;
      
      if (finishTotalMinutes <= startTotalMinutes) {
        setNotification({ 
          show: true, 
          message: 'Waktu selesai tidak boleh lebih awal dari waktu mulai. Untuk kegiatan lintas tengah malam, silakan buat laporan terpisah.', 
          type: 'error' 
        });
        setLoading(false);
        return;
      }
    }
    
    const dataToSend = new FormData();
    dataToSend.append('start_overtime_date', formData.start_overtime_date);
    dataToSend.append('end_overtime_date', formData.end_overtime_date);
    dataToSend.append('start_time', formData.start_time);
    // Only send finish_time if it has a value to handle database constraint
    if (formData.finish_time && formData.finish_time.trim() !== '') {
      dataToSend.append('finish_time', formData.finish_time);
    }
    dataToSend.append('overtime_hours', formData.overtime_hours);
    dataToSend.append('purpose', formData.purpose);
    dataToSend.append('type_or_work', formData.type_or_work);
    dataToSend.append('teacher_name', formData.teacher_name);
    if (formData.teacher_id) {
      dataToSend.append('teacher_id', formData.teacher_id);
    }
    if (formData.student_id) {
      // Send as student_ids array as expected by the backend
      dataToSend.append('student_ids[]', formData.student_id);
    }

    try {
      const response = await authFetch('/api/teacher/lapor-lembur', {
        method: 'POST',
        body: dataToSend
      });
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setNotification({ show: true, message: data.message || 'Gagal mengirim laporan lembur.', type: 'error' });
      } else {
        setNotification({ show: true, message: data.message || 'Laporan lembur berhasil dikirim!', type: 'success' });
        setFormData({
          start_overtime_date: '',
          end_overtime_date: '',
          start_time: '',
          finish_time: '',
          overtime_hours: '',
          purpose: '',
          type_or_work: '',
          teacher_name: '',
          teacher_id: '',
          student_id: ''
        });
        setSelectedTeacherId(null);
        setSelectedStudent(null);
        setStudentSearch('');
        setShowSuggestions(false);
        setTeacherSuggestions([]);
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan.', type: 'error' });
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>Formulir Laporan Lembur</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>
      
      <FormContent>
        <form onSubmit={handleSubmit}>
          {notification.show && (
            <NotificationToast $type={notification.type}>
              <NotificationMessage>{notification.message}</NotificationMessage>
              <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
                ×
              </CloseNotificationButton>
            </NotificationToast>
          )}
          
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="student_search">Nama Siswa</label>
            <StudentSuggestContainer>
              <input
                type="text"
                id="student_search"
                name="student_search"
                value={studentSearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setStudentSearch(value);
                  setShowStudentSuggestions(value.length > 0);
                  // Clear selected student if user starts typing a new name
                  if (selectedStudent && value !== selectedStudent.name) {
                    setSelectedStudent(null);
                    setFormData({ ...formData, student_id: '' });
                  }
                }}
                onFocus={() => {
                  if (studentSearch.length > 0) {
                    setShowStudentSuggestions(true);
                  }
                }}
                onBlur={handleStudentInputBlur}
                placeholder="Ketik nama siswa..."
                readOnly={readOnly}
                disabled={readOnly}
                autoComplete="off"
              />
              {showStudentSuggestions && studentSearch.length > 0 && !readOnly && (
                <SuggestionsList>
                  {students
                    .filter(student => 
                      student.name.toLowerCase().includes(studentSearch.toLowerCase()) &&
                      (!selectedStudent || selectedStudent.id !== student.id)
                    )
                    .slice(0, 10)
                    .map((student) => (
                      <SuggestionItem
                        key={student.id}
                        onClick={() => selectStudent(student)}
                      >
                        <div>
                          <strong>{student.name}</strong>
                          <div style={{ fontSize: '0.9em', color: 'var(--text-light)' }}>
                            NIS {student.nis} - Kelas {student.class}
                          </div>
                        </div>
                      </SuggestionItem>
                    ))
                  }
                  {students.filter(student => 
                    student.name.toLowerCase().includes(studentSearch.toLowerCase()) &&
                    (!selectedStudent || selectedStudent.id !== student.id)
                  ).length === 0 && (
                    <SuggestionItem key="no-students-found" style={{ fontStyle: 'italic', color: 'var(--text-light)' }}>
                      Tidak ada siswa ditemukan
                    </SuggestionItem>
                  )}
                </SuggestionsList>
              )}
            </StudentSuggestContainer>
            {selectedStudent && (
              <SelectedStudentsContainer>
                <SelectedStudentTag>
                  <span>{selectedStudent.name} ({selectedStudent.class})</span>
                  {!readOnly && (
                    <RemoveStudentButton
                      type="button"
                      onClick={clearSelectedStudent}
                    >
                      ×
                    </RemoveStudentButton>
                  )}
                </SelectedStudentTag>
              </SelectedStudentsContainer>
            )}
          </FormGroup>
          
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="start_overtime_date">Tanggal Lembur Mulai</label>
            <input
              type="date"
              id="start_overtime_date"
              name="start_overtime_date"
              value={formData.start_overtime_date}
              onChange={handleChange}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="end_overtime_date">Tanggal Lembur Selesai</label>
            <input
              type="date"
              id="end_overtime_date"
              name="end_overtime_date"
              value={formData.end_overtime_date}
              onChange={handleChange}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="start_time">Jam Mulai</label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="finish_time">Jam Selesai</label>
              <input
                type="time"
                id="finish_time"
                name="finish_time"
                value={formData.finish_time}
                onChange={handleChange}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="overtime_hours">Jumlah Jam Lembur</label>
            <input
              type="number"
              id="overtime_hours"
              name="overtime_hours"
              value={formData.overtime_hours}
              onChange={handleChange}
              placeholder="Contoh: 8"
              min="0"
              step="0.5"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="purpose">Alasan Lembur / Tujuan</label>
            <input
              type="text"
              id="purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Contoh: Ada pekerjaan tambahan"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="type_or_work">Jenis Pekerjaan</label>
            <input
              type="text"
              id="type_or_work"
              name="type_or_work"
              value={formData.type_or_work}
              onChange={handleChange}
              placeholder="Contoh: Membersihkan ruang bengkel, memperbaiki alat"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="teacher_name">Nama Guru Bengkel / Pembimbing</label>
            <TeacherSuggestContainer>
              <input
                type="text"
                id="teacher_name"
                name="teacher_name"
                value={formData.teacher_name}
                onChange={handleChange}
                onBlur={handleTeacherInputBlur}
                onFocus={() => {
                  if (formData.teacher_name.trim() && teacherSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Ketik nama guru..."
                required
                readOnly={readOnly}
                disabled={readOnly}
                autoComplete="off"
              />
              {showSuggestions && teacherSuggestions.length > 0 && !readOnly && (
                <SuggestionsDropdown>
                  {teacherSuggestions.map((teacher) => (
                    <SuggestionItem
                      key={teacher.id}
                      onClick={() => handleTeacherSelect(teacher)}
                      selected={selectedTeacherId === teacher.id}
                    >
                      <TeacherName>{teacher.name}</TeacherName>
                    </SuggestionItem>
                  ))}
                </SuggestionsDropdown>
              )}
            </TeacherSuggestContainer>
          </FormGroup>
          
          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading || readOnly}>
                {loading ? 'Mengirim...' : (readOnly ? 'Tutup' : 'Kirim Laporan')}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #6c757d;
    
    &:hover {
      background-color: #6c757d;
    }
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

// Notification Toast styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const TeacherSuggestContainer = styled.div`
  position: relative;
`;

const StudentSuggestContainer = styled.div`
  position: relative;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  transition: background-color 0.2s;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  
  &:hover {
    background-color: var(--background-light);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TeacherName = styled.div`
  font-weight: 500;
  color: var(--text-dark);
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SelectedStudentsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SelectedStudentTag = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--primary);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  gap: 0.25rem;
`;

const RemoveStudentButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

export default LaporLemburForm;